
import { setGlobalConfig } from "./GlobalConfig.js";
import { animateGridPattern, invalidateTrimCache } from './effects/GridAnimatorEffects.js';
import { generatePattern } from './effects/ShapePatternEffects.js';

import '../scss/styles.scss';

// Expose effect engine for inline Blocks card animations
window._blocksEffect = { animateGridPattern, invalidateTrimCache, generatePattern };
import { LiteGraph, LGraph, LGraphCanvas } from 'litegraph.js';

// Import nodes — Input
import { FontPickerNode } from "./nodes/FontPickerNode.js";
import { TextInputNode } from "./nodes/TextInputNode.js";
import { DrawingInputNode } from "./nodes/DrawingInputNode.js";
import { ColorNode } from "./nodes/ColorNode.js";

// Import nodes — Processing
import { TextRenderNode } from "./nodes/TextRenderNode.js";
import { BounceNode } from "./nodes/BounceNode.js";
import { PatternNode } from "./nodes/PatternNode.js";
import { ShapePatternNode } from "./nodes/ShapePatternNode.js";
import { TimingFunctionNode } from "./nodes/TimingFunctionNode.js";

// Import nodes — Output
import { GridAnimatorNode } from "./nodes/GridAnimatorNode.js";
import { ImageRenderNode } from "./nodes/ImageRenderNode.js";
import { MergeNode } from "./nodes/MergeNode.js";

// Import nodes — Generators
import { OscillatorNode } from "./nodes/OscillatorNode.js";
import { WindNode } from "./nodes/WindNode.js";
import { AudioReactiveNode } from "./nodes/AudioReactiveNode.js";

// Import nodes — Effects
import { GlitchNode } from "./nodes/GlitchNode.js";
import { WaveDistortNode } from "./nodes/WaveDistortNode.js";
import { PixelSortNode } from "./nodes/PixelSortNode.js";
import { FeedbackNode } from "./nodes/FeedbackNode.js";
import { TypewriterNode } from "./nodes/TypewriterNode.js";
import { ThresholdNode } from "./nodes/ThresholdNode.js";
import { MirrorNode } from "./nodes/MirrorNode.js";
import { HueShiftNode } from "./nodes/HueShiftNode.js";
import { TileNode } from "./nodes/TileNode.js";

// Import nodes — Composite
import { BlendModeNode } from "./nodes/BlendModeNode.js";
import { MaskNode } from "./nodes/MaskNode.js";

// Import nodes — Generators (new)
import { NoiseNode } from "./nodes/NoiseNode.js";
import { SpringNode } from "./nodes/SpringNode.js";
import { ParticleEmitterNode } from "./nodes/ParticleEmitterNode.js";
import { StaggerNode } from "./nodes/StaggerNode.js";
import { ClockNode } from "./nodes/ClockNode.js";
import { SequencerNode } from "./nodes/SequencerNode.js";
import { RandomNode } from "./nodes/RandomNode.js";
import { GradientNode } from "./nodes/GradientNode.js";

// Import nodes — Input (new)
import { ImageInputNode } from "./nodes/ImageInputNode.js";
import { MouseNode } from "./nodes/MouseNode.js";
import { MouseTrailNode } from "./nodes/MouseTrailNode.js";
import { ScrollNode } from "./nodes/ScrollNode.js";

// Import nodes — Utility
import { MathNode } from "./nodes/MathNode.js";
import { RemapNode } from "./nodes/RemapNode.js";

// Import nodes — Export
import { RecorderNode } from "./nodes/RecorderNode.js";
import { CameraNode } from "./nodes/CameraNode.js";
import { FontExportNode } from "./nodes/FontExportNode.js";

// fontExporter lazy-loaded — pulls in opentype.js, imagetracerjs, svg-path-parser (~7MB)

const gifSize = 500;


// DOM references
const nodeEditor = document.getElementById('node-editor');
const recordBtn = document.getElementById('record-btn');
const exportFormat = document.getElementById('export-format');

const gifCanvas = document.createElement('canvas');
gifCanvas.width = gifSize;
gifCanvas.height = gifSize;
const gifCtx = gifCanvas.getContext('2d');

const glob = {
  graph: new LGraph(),
  graphCanvas: null,
  outputNode: null
};
window.glob = glob;

// Register nodes — Input
LiteGraph.registerNodeType("input/FontPicker", FontPickerNode);
LiteGraph.registerNodeType("input/TextInput", TextInputNode);
LiteGraph.registerNodeType("input/Color", ColorNode);
LiteGraph.registerNodeType("draw/DrawingInput", DrawingInputNode);

// Register nodes — Processing
LiteGraph.registerNodeType("custom/TextRender", TextRenderNode);
LiteGraph.registerNodeType("transform/Bounce", BounceNode);
LiteGraph.registerNodeType("custom/PatternGenerator", PatternNode);
LiteGraph.registerNodeType("custom/ShapePatternNode", ShapePatternNode);
LiteGraph.registerNodeType("custom/TimingFunction", TimingFunctionNode);

// Register nodes — Output
LiteGraph.registerNodeType("custom/GridAnimator", GridAnimatorNode);
LiteGraph.registerNodeType("custom/ImageRenderNode", ImageRenderNode);
LiteGraph.registerNodeType("composite/Merge", MergeNode);

// Register nodes — Generators
LiteGraph.registerNodeType("generators/Oscillator", OscillatorNode);
LiteGraph.registerNodeType("generators/WindData", WindNode);
LiteGraph.registerNodeType("generators/AudioReactive", AudioReactiveNode);

// Register nodes — Effects
LiteGraph.registerNodeType("effects/Glitch", GlitchNode);
LiteGraph.registerNodeType("effects/WaveDistort", WaveDistortNode);
LiteGraph.registerNodeType("effects/PixelSort", PixelSortNode);
LiteGraph.registerNodeType("effects/Feedback", FeedbackNode);
LiteGraph.registerNodeType("effects/Typewriter", TypewriterNode);
LiteGraph.registerNodeType("effects/Threshold", ThresholdNode);
LiteGraph.registerNodeType("effects/Mirror", MirrorNode);
LiteGraph.registerNodeType("effects/HueShift", HueShiftNode);
LiteGraph.registerNodeType("effects/Tile", TileNode);

// Register nodes — Composite (new)
LiteGraph.registerNodeType("composite/BlendMode", BlendModeNode);
LiteGraph.registerNodeType("composite/Mask", MaskNode);

// Register nodes — Generators (new)
LiteGraph.registerNodeType("generators/Noise", NoiseNode);
LiteGraph.registerNodeType("generators/Spring", SpringNode);
LiteGraph.registerNodeType("generators/Particles", ParticleEmitterNode);
LiteGraph.registerNodeType("generators/Stagger", StaggerNode);
LiteGraph.registerNodeType("generators/Clock", ClockNode);
LiteGraph.registerNodeType("generators/Sequencer", SequencerNode);
LiteGraph.registerNodeType("generators/Random", RandomNode);
LiteGraph.registerNodeType("generators/Gradient", GradientNode);

// Register nodes — Input (new)
LiteGraph.registerNodeType("input/ImageInput", ImageInputNode);
LiteGraph.registerNodeType("input/Mouse", MouseNode);
LiteGraph.registerNodeType("input/MouseTrail", MouseTrailNode);
LiteGraph.registerNodeType("input/Scroll", ScrollNode);

// Register nodes — Utility
LiteGraph.registerNodeType("utility/Math", MathNode);
LiteGraph.registerNodeType("utility/Remap", RemapNode);

// Register nodes — Export
LiteGraph.registerNodeType("export/Recorder", RecorderNode);
LiteGraph.registerNodeType("export/Camera", CameraNode);
LiteGraph.registerNodeType("export/FontExport", FontExportNode);

// Recording state
let gif;

let recording = false;
let zip;
let frameCounter = 0;
let recordedBlobs = [];
let mediaRecorder;
let testCanvas = document.createElement('canvas');
testCanvas.width = 3000;
testCanvas.height = 3000;
const testCtx = testCanvas.getContext('2d');

// Initialize graph and nodes
function buildChart() {

  let colour = getUrlParam("animationColour") || '#010101';
  const brushPicker = document.getElementById("brushColorPicker");
  if (brushPicker) brushPicker.value = colour;
  setGlobalConfig("brushColor", colour);

  const wrap = document.getElementById('node-wrap');
  nodeEditor.width = wrap.clientWidth;
  nodeEditor.height = wrap.clientHeight;
  glob.graphCanvas = new LGraphCanvas(nodeEditor, glob.graph);
  glob.graphCanvas.resize();
  glob.graphCanvas.render_canvas_border = false;

  // Canvas appearance
  glob.graphCanvas.clear_background_color = "#1a1a1a";
  glob.graphCanvas.render_shadows = false;
  glob.graphCanvas.render_curved_connections = true;
  glob.graphCanvas.connections_width = 3;
  glob.graphCanvas.render_connection_arrows = false;

  // Neon bright cables — link line colors by type
  LGraphCanvas.link_type_colors = {
    "-1":     "#ff6600",   // event
    "canvas": "#00ff6a",   // green
    "string": "#00d4ff",   // cyan
    "number": "#ffe600",   // yellow
    "array":  "#ff00ff",   // magenta
  };

  // Slot dot colors (match cables)
  glob.graphCanvas.default_connection_color_byType = {
    "canvas": "#00ff6a",
    "string": "#00d4ff",
    "number": "#ffe600",
    "array":  "#ff00ff",
  };

  glob.graphCanvas.default_connection_color_byTypeOff = {
    "canvas": "#00994a",
    "string": "#0088aa",
    "number": "#aa9900",
    "array":  "#990099",
  };

  // Default / fallback link colors
  LiteGraph.LINK_COLOR = "#ff3366";
  LiteGraph.EVENT_LINK_COLOR = "#ff6600";
  LiteGraph.CONNECTING_LINK_COLOR = "#00ff6a";

  // Priority: URL hash (shared graph) > preset param > default
  let restored = false;

  if (window.location.hash.length > 1) {
    restored = loadGraphFromHash();
  }

  if (!restored) {
    const presetName = getUrlParam("preset") || "default";
    const base = buildBaseGraph();
    const presetFn = GRAPH_PRESETS[presetName] || GRAPH_PRESETS["default"];
    presetFn(base);

    document.querySelectorAll('.preset-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.preset === presetName);
    });
  }

  glob.graph.start();
  requestAnimationFrame(updateDisplay);
  // Fit all nodes to the visible canvas after a short delay so widgets measure
  setTimeout(fitGraphToView, 50);
}

// Compute bounding box of all nodes and zoom/pan so they all fit on screen
function fitGraphToView() {
  if (!glob.graphCanvas || !glob.graph._nodes || !glob.graph._nodes.length) return;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const n of glob.graph._nodes) {
    const x = n.pos[0];
    const y = n.pos[1] - 22; // include title bar
    const w = n.size[0];
    const h = n.size[1] + 22;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  }

  const pad = 40;
  // Account for the preset panel / article tabs on the right edge (~48px wide)
  const rightInset = 60;
  const canvasEl = glob.graphCanvas.canvas;
  const viewW = canvasEl.width - rightInset;
  const viewH = canvasEl.height;

  const graphW = (maxX - minX) + pad * 2;
  const graphH = (maxY - minY) + pad * 2;

  // Fit to view, then zoom in a bit extra so nodes fill the space
  const fitScale = Math.min(viewW / graphW, viewH / graphH, 1);
  const scale = Math.min(fitScale * 1.25, 1.2);
  glob.graphCanvas.ds.scale = scale;
  glob.graphCanvas.ds.offset[0] = -(minX - pad) + (viewW / scale - graphW) / 2;
  glob.graphCanvas.ds.offset[1] = -(minY - pad) + (viewH / scale - graphH) / 2;
  glob.graphCanvas.setDirty(true, true);
}
window.fitGraphToView = fitGraphToView;

// ─── Preset System ──────────────────────────────────────────────────────

function buildBaseGraph() {
  // Layout: 4 columns flowing left → right
  // Col A (x=60):  text/font sources
  // Col B (x=280): text render
  // Col C (x=530): bounce, pattern, timing, color (all gridAnim feeders stacked)
  // Col D (x=850): grid animator output
  //
  // Generators in presets go below fontNode in Col A/B, feeding right into Col C.
  const textNode     = createNode("input/TextInput",         60, 60);
  const fontNode     = createNode("input/FontPicker",        60, 200);
  const textRend     = createNode("custom/TextRender",       280, 60);
  const bounceNode   = createNode("transform/Bounce",        530, 200);
  const patternNode  = createNode("custom/ShapePatternNode", 530, 420);
  const timingNode   = createNode("custom/TimingFunction",   530, 600);
  const colorNode    = createNode("input/Color",             530, 860);
  const gridAnim     = createNode("custom/GridAnimator",     850, 150);

  textNode.connect(0, textRend, 0);
  fontNode.connect(0, textRend, 1);
  textRend.connect(0, bounceNode, 0);
  bounceNode.connect(0, gridAnim, 0);
  patternNode.connect(0, gridAnim, 1);
  timingNode.connect(0, gridAnim, 2);
  colorNode.connect(0, gridAnim, 3);

  glob.outputNode = gridAnim;

  import(/* webpackChunkName: "fontExporter" */ './fontExporter.js')
    .then(m => m.default(textNode, timingNode))
    .catch(err => console.warn('[app.js] fontExporter lazy-load failed:', err));

  return { textNode, fontNode, textRend, bounceNode, patternNode, colorNode, timingNode, gridAnim };
}

// Reposition all base nodes — each preset gets its own layout
function layoutNodes(base, l) {
  for (const k in l) { if (base[k]) base[k].pos = l[k]; }
}

// Set node properties and sync widgets to match
function setProps(node, props) {
  Object.assign(node.properties, props);
  if (node.widgets) {
    node.widgets.forEach(w => {
      if (props[w.name] !== undefined) w.value = props[w.name];
      // Match widget names that differ from property keys
      const propKey = Object.keys(props).find(k => k.toLowerCase() === w.name.toLowerCase());
      if (propKey !== undefined) w.value = props[propKey];
    });
  }
}

const GRAPH_PRESETS = {

  "default": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [800, 120], patternNode: [800, 340], timingNode: [800, 520],
      colorNode: [800, 770], gridAnim: [1080, 50],
    });

    // Black background + white grid
    base.gridAnim.properties.bgColor = "#000000";
    if (base.gridAnim.widgets) {
      base.gridAnim.widgets.forEach(w => { if (w.name === "Background") w.value = "#000000"; });
    }
    base.colorNode.properties.color = "#ffffff";
    if (base.colorNode.widgets) {
      base.colorNode.widgets.forEach(w => { if (w.name === "Hex") w.value = "#ffffff"; });
    }

    // Load the Frontify logo as an image and composite it above the text
    const logo = createNode("input/ImageInput", 300, 230);
    logo.properties.src = "assets/images/logo.png";
    logo.properties.canvasWidth = 1080;
    logo.properties.canvasHeight = 1080;
    logo.properties.imageWidth = 180;
    logo.properties.offsetY = 280;  // Above text (text rendered around y=540)
    logo.loadFromUrl("assets/images/logo.png");

    // Merge logo canvas over the text canvas
    const merge = createNode("composite/Merge", 555, 45);
    merge.properties.blend = "source-over";
    merge.properties.opacity = 1;
    merge.properties.offsetAY = 80;   // shift text down
    merge.properties.offsetBY = 0;
    if (merge.widgets) {
      merge.widgets.forEach(w => {
        if (w.name === "Blend") w.value = "source-over";
        if (w.name === "Opacity B") w.value = 1;
        if (w.name === "Offset A Y") w.value = 80;
        if (w.name === "Offset B Y") w.value = 0;
      });
    }
    base.textRend.disconnectOutput(0);
    base.textRend.connect(0, merge, 0);   // Canvas A: text
    logo.connect(0, merge, 1);            // Canvas B: logo
    merge.connect(0, base.bounceNode, 0); // Merged → bounce
  },

  "blocks": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [555, 120], patternNode: [555, 340], timingNode: [555, 520],
      colorNode: [555, 740], gridAnim: [830, 50],
    });
    setProps(base.textNode, { text: "Good Work" });
    setProps(base.textRend, { fontSize: 200 });
    setProps(base.bounceNode, { width: 6, height: 6 });
    setProps(base.patternNode, { shapeType: "square", patternName: "random", gridCount: 160, cellScale: 1 });
  },

  "up-front": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [555, 120], patternNode: [555, 340], timingNode: [555, 520],
      colorNode: [555, 740], gridAnim: [830, 50],
    });
    setProps(base.textNode, { text: "Fresh Takes" });
    setProps(base.textRend, { fontSize: 200 });
    setProps(base.bounceNode, { width: 6, height: 6 });
    setProps(base.patternNode, { shapeType: "circle", cellScale: 1.3, gridCount: 151 });
  },

  "visionaries": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [555, 120], patternNode: [555, 340], timingNode: [555, 520],
      colorNode: [555, 740], gridAnim: [830, 50],
    });
    setProps(base.textNode, { text: "Long Views" });
    setProps(base.textRend, { fontSize: 200 });
    setProps(base.bounceNode, { width: 6, height: 6 });
    setProps(base.patternNode, { shapeType: "expressive-brush", cellScale: 2.3, gridCount: 200 });
  },

  "paradigm-shifts": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [555, 120], patternNode: [555, 340], timingNode: [555, 520],
      colorNode: [555, 740], gridAnim: [830, 50],
    });
    setProps(base.textNode, { text: "Big Trends" });
    setProps(base.textRend, { fontSize: 200 });
    setProps(base.patternNode, { shapeType: "hexagon", cellScale: 1.4, gridCount: 110 });
  },

  "wind-blown": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 175], textRend: [305, 45],
      bounceNode: [560, 115], patternNode: [560, 350], timingNode: [560, 530],
      colorNode: [560, 750], gridAnim: [835, 45],
    });
    const wind = createNode("generators/WindData", 305, 310);
    base.timingNode.connect(0, wind, 0);
    wind.connect(0, base.bounceNode, 1);
    wind.connect(1, base.bounceNode, 2);
    base.bounceNode.properties.width = 0;
    base.bounceNode.properties.height = 0;
    if (base.bounceNode.widgets) {
      base.bounceNode.widgets.forEach(w => {
        if (w.name === "Width") w.value = 0;
        if (w.name === "Height") w.value = 0;
      });
    }
  },

  "audio-reactive": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 165], textRend: [300, 50],
      bounceNode: [555, 125], patternNode: [555, 345], timingNode: [555, 525],
      colorNode: [555, 745], gridAnim: [830, 55],
    });
    const audio = createNode("generators/AudioReactive", 50, 320);
    audio.properties.active = true;
    audio.properties.gain = 4;
    audio.properties.smoothing = 0.6;

    // Remap 0-1 audio outputs to useful ranges
    const remapW = createNode("utility/Remap", 50, 490);
    remapW.properties.inMin = 0; remapW.properties.inMax = 1;
    remapW.properties.outMin = 0; remapW.properties.outMax = 80;
    if (remapW.widgets) {
      remapW.widgets.forEach(w => {
        if (w.name === "Out Max") w.value = 80;
      });
    }

    const remapH = createNode("utility/Remap", 280, 490);
    remapH.properties.inMin = 0; remapH.properties.inMax = 1;
    remapH.properties.outMin = 0; remapH.properties.outMax = 60;
    if (remapH.widgets) {
      remapH.widgets.forEach(w => {
        if (w.name === "Out Max") w.value = 60;
      });
    }

    const remapSize = createNode("utility/Remap", 280, 320);
    remapSize.properties.inMin = 0; remapSize.properties.inMax = 1;
    remapSize.properties.outMin = 150; remapSize.properties.outMax = 300;
    if (remapSize.widgets) {
      remapSize.widgets.forEach(w => {
        if (w.name === "Out Min") w.value = 150; if (w.name === "Out Max") w.value = 300;
      });
    }

    audio.connect(0, remapSize, 0);   // amplitude → font size
    audio.connect(1, remapW, 0);      // bass → bounce width
    audio.connect(2, remapH, 0);      // treble → bounce height
    remapSize.connect(0, base.textRend, 2);
    remapW.connect(0, base.bounceNode, 3);
    remapH.connect(0, base.bounceNode, 4);
  },

  "pulse": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 48],
      bounceNode: [560, 120], patternNode: [560, 340], timingNode: [560, 520],
      colorNode: [560, 740], gridAnim: [835, 50],
    });
    const osc = createNode("generators/Oscillator", 50, 315);
    osc.properties.min = 150;
    osc.properties.max = 250;
    osc.properties.frequency = 0.3;
    osc.properties.waveform = "sine";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 150; if (w.name === "Max") w.value = 250; if (w.name === "Freq (Hz)") w.value = 0.3; });
    }
    osc.connect(0, base.textRend, 2);
  },

  "breathing": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 168], textRend: [298, 46],
      bounceNode: [558, 118], patternNode: [558, 345], timingNode: [558, 525],
      colorNode: [558, 745], gridAnim: [832, 48],
    });
    const osc = createNode("generators/Oscillator", 300, 310);
    osc.properties.min = 0.8;
    osc.properties.max = 1.6;
    osc.properties.frequency = 0.15;
    osc.properties.waveform = "sine";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 0.8; if (w.name === "Max") w.value = 1.6; if (w.name === "Freq (Hz)") w.value = 0.15; });
    }
    osc.connect(0, base.patternNode, 2);
  },

  "jitter": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 172], textRend: [302, 48],
      bounceNode: [560, 122], patternNode: [560, 348], timingNode: [560, 528],
      colorNode: [560, 748], gridAnim: [835, 52],
    });
    const oscW = createNode("generators/Oscillator", 50, 318);
    oscW.properties.min = 0;   oscW.properties.max = 40;
    oscW.properties.frequency = 4; oscW.properties.waveform = "saw";
    if (oscW.widgets) {
      oscW.widgets.forEach(w => { if (w.name === "Min") w.value = 0; if (w.name === "Max") w.value = 40; if (w.name === "Freq (Hz)") w.value = 4; });
    }

    const oscH = createNode("generators/Oscillator", 300, 318);
    oscH.properties.min = 0;   oscH.properties.max = 30;
    oscH.properties.frequency = 5.5; oscH.properties.waveform = "square";
    if (oscH.widgets) {
      oscH.widgets.forEach(w => { if (w.name === "Min") w.value = 0; if (w.name === "Max") w.value = 30; if (w.name === "Freq (Hz)") w.value = 5.5; });
    }

    oscW.connect(0, base.bounceNode, 3);
    oscH.connect(0, base.bounceNode, 4);
    setProps(base.timingNode, { duration: 100 });
  },

  "fm-synth": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [555, 120], patternNode: [555, 340], timingNode: [555, 520],
      colorNode: [555, 740], gridAnim: [830, 50],
    });
    const modulator = createNode("generators/Oscillator", 50, 315);
    modulator.properties.min = 0.1; modulator.properties.max = 3;
    modulator.properties.frequency = 0.2; modulator.properties.waveform = "sine";
    if (modulator.widgets) {
      modulator.widgets.forEach(w => { if (w.name === "Min") w.value = 0.1; if (w.name === "Max") w.value = 3; if (w.name === "Freq (Hz)") w.value = 0.2; });
    }

    const carrier = createNode("generators/Oscillator", 50, 480);
    carrier.properties.min = 100; carrier.properties.max = 300;
    carrier.properties.frequency = 1; carrier.properties.waveform = "triangle";
    if (carrier.widgets) {
      carrier.widgets.forEach(w => { if (w.name === "Min") w.value = 100; if (w.name === "Max") w.value = 300; if (w.name === "Freq (Hz)") w.value = 1; });
    }

    modulator.connect(0, carrier, 2);
    carrier.connect(0, base.textRend, 2);
    setProps(base.timingNode, { duration: 200 });
  },

  "storm": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 48],
      bounceNode: [560, 120], patternNode: [560, 345], timingNode: [560, 525],
      colorNode: [560, 745], gridAnim: [835, 50],
    });
    const wind = createNode("generators/WindData", 50, 320);
    wind.connect(0, base.bounceNode, 1);
    wind.connect(1, base.bounceNode, 2);

    const osc = createNode("generators/Oscillator", 50, 565);
    osc.properties.min = 80; osc.properties.max = 200;
    osc.properties.frequency = 0.5; osc.properties.waveform = "triangle";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 80; if (w.name === "Max") w.value = 200; if (w.name === "Freq (Hz)") w.value = 0.5; });
    }

    wind.connect(2, base.bounceNode, 3);
    osc.connect(0, base.patternNode, 1);
  },

  "voice-form": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 168], textRend: [298, 46],
      bounceNode: [556, 118], patternNode: [556, 340], timingNode: [556, 520],
      colorNode: [556, 740], gridAnim: [830, 48],
    });
    const audio = createNode("generators/AudioReactive", 50, 320);
    audio.properties.active = true;
    audio.properties.gain = 2;

    const osc = createNode("generators/Oscillator", 50, 480);
    osc.properties.min = 100; osc.properties.max = 250;
    osc.properties.frequency = 0.5;
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 100; if (w.name === "Max") w.value = 250; if (w.name === "Freq (Hz)") w.value = 0.5; });
    }

    audio.connect(0, osc, 1);
    osc.connect(0, base.textRend, 2);
    audio.connect(1, base.patternNode, 2);
    audio.connect(2, base.bounceNode, 5);
  },

  // ─── New presets showcasing new nodes ───────────────────────────────

  "glitch-out": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 172], textRend: [300, 48],
      bounceNode: [555, 120], patternNode: [555, 345], timingNode: [555, 528],
      colorNode: [555, 748], gridAnim: [1055, 50],
    });
    const glitch = createNode("effects/Glitch", 790, 130);
    const osc = createNode("generators/Oscillator", 50, 318);
    osc.properties.min = 0; osc.properties.max = 1;
    osc.properties.frequency = 0.4; osc.properties.waveform = "saw";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 0; if (w.name === "Max") w.value = 1; if (w.name === "Freq (Hz)") w.value = 0.4; });
    }
    base.bounceNode.connect(0, glitch, 0);
    glitch.connect(0, base.gridAnim, 0);
    osc.connect(0, glitch, 1);
  },

  "echo": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 168], textRend: [298, 46],
      bounceNode: [558, 118], patternNode: [558, 342], timingNode: [558, 522],
      colorNode: [558, 742], gridAnim: [1050, 48],
    });
    const feedback = createNode("effects/Feedback", 792, 128);
    feedback.properties.decay = 0.92;
    feedback.properties.offsetX = 5;
    feedback.properties.offsetY = 3;
    if (feedback.widgets) {
      feedback.widgets.forEach(w => {
        if (w.name === "Decay") w.value = 0.92;
      });
    }
    base.bounceNode.connect(0, feedback, 0);
    feedback.connect(0, base.gridAnim, 0);
  },

  "wave": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 48],
      bounceNode: [555, 122], patternNode: [555, 345], timingNode: [555, 528],
      colorNode: [555, 748], gridAnim: [1058, 50],
    });
    const wave = createNode("effects/WaveDistort", 790, 132);
    wave.properties.amplitude = 40;
    wave.properties.frequency = 0.015;
    if (wave.widgets) {
      wave.widgets.forEach(w => {
        if (w.name === "Direction") w.value = "both";
      });
    }
    wave.properties.direction = "both";
    const remap = createNode("utility/Remap", 300, 318);
    remap.properties.inMin = 0; remap.properties.inMax = 1;
    remap.properties.outMin = 0; remap.properties.outMax = 20;
    if (remap.widgets) {
      remap.widgets.forEach(w => {
        if (w.name === "Out Max") w.value = 20;
      });
    }
    base.timingNode.connect(0, remap, 0);
    remap.connect(0, wave, 3);
    base.bounceNode.connect(0, wave, 0);
    wave.connect(0, base.gridAnim, 0);
  },

  "mouse-play": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 48],
      bounceNode: [558, 115], patternNode: [558, 342], timingNode: [558, 522],
      colorNode: [558, 742], gridAnim: [832, 48],
    });
    const mouse = createNode("input/Mouse", 50, 320);
    const remapX = createNode("utility/Remap", 280, 320);
    remapX.properties.outMin = -80; remapX.properties.outMax = 80;
    if (remapX.widgets) {
      remapX.widgets.forEach(w => {
        if (w.name === "Out Min") w.value = -80; if (w.name === "Out Max") w.value = 80;
      });
    }
    const remapY = createNode("utility/Remap", 280, 495);
    remapY.properties.outMin = -60; remapY.properties.outMax = 60;
    if (remapY.widgets) {
      remapY.widgets.forEach(w => {
        if (w.name === "Out Min") w.value = -60; if (w.name === "Out Max") w.value = 60;
      });
    }
    mouse.connect(0, remapX, 0);
    mouse.connect(1, remapY, 0);
    remapX.connect(0, base.bounceNode, 1);
    remapY.connect(0, base.bounceNode, 2);
    const remapSpd = createNode("utility/Remap", 50, 560);
    remapSpd.properties.outMin = 150; remapSpd.properties.outMax = 300;
    if (remapSpd.widgets) {
      remapSpd.widgets.forEach(w => {
        if (w.name === "Out Min") w.value = 150; if (w.name === "Out Max") w.value = 300;
      });
    }
    const spring = createNode("generators/Spring", 50, 735);
    spring._value = 200;  // Start at base font size so text is visible immediately
    mouse.connect(2, remapSpd, 0);
    remapSpd.connect(0, spring, 0);
    spring.connect(0, base.textRend, 2);
    base.bounceNode.properties.width = 0;
    base.bounceNode.properties.height = 0;
    if (base.bounceNode.widgets) {
      base.bounceNode.widgets.forEach(w => {
        if (w.name === "Width") w.value = 0;
        if (w.name === "Height") w.value = 0;
      });
    }
  },

  "pixel-sort": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [302, 46],
      bounceNode: [556, 120], patternNode: [556, 342], timingNode: [556, 525],
      colorNode: [556, 745], gridAnim: [1055, 48],
    });
    const sort = createNode("effects/PixelSort", 790, 128);
    sort.properties.threshold = 0.2;
    sort.properties.upperThreshold = 0.7;
    if (sort.widgets) {
      sort.widgets.forEach(w => {
        if (w.name === "Direction") w.value = "horizontal";
      });
    }
    const osc = createNode("generators/Oscillator", 50, 315);
    osc.properties.min = 0.1; osc.properties.max = 0.5;
    osc.properties.frequency = 0.3; osc.properties.waveform = "sine";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 0.1; if (w.name === "Max") w.value = 0.5; if (w.name === "Freq (Hz)") w.value = 0.3; });
    }
    base.bounceNode.connect(0, sort, 0);
    sort.connect(0, base.gridAnim, 0);
    osc.connect(0, sort, 1);
  },

  "bouncy": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 168], textRend: [298, 48],
      bounceNode: [558, 118], patternNode: [558, 345], timingNode: [558, 525],
      colorNode: [558, 745], gridAnim: [832, 50],
    });
    const osc = createNode("generators/Oscillator", 50, 318);
    osc.properties.min = -40; osc.properties.max = 40;
    osc.properties.frequency = 0.8; osc.properties.waveform = "square";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = -40; if (w.name === "Max") w.value = 40; if (w.name === "Freq (Hz)") w.value = 0.8; });
    }
    const springX = createNode("generators/Spring", 300, 315);
    springX.properties.tension = 0.15;
    springX.properties.friction = 0.82;
    if (springX.widgets) {
      springX.widgets.forEach(w => {
        if (w.name === "Tension") w.value = 0.15; if (w.name === "Friction") w.value = 0.82;
      });
    }
    const springY = createNode("generators/Spring", 300, 438);
    springY.properties.tension = 0.2;
    springY.properties.friction = 0.78;
    if (springY.widgets) {
      springY.widgets.forEach(w => {
        if (w.name === "Tension") w.value = 0.2; if (w.name === "Friction") w.value = 0.78;
      });
    }
    osc.connect(0, springX, 0);
    osc.connect(0, springY, 0);
    springX.connect(0, base.bounceNode, 1);
    springY.connect(0, base.bounceNode, 2);
    base.bounceNode.properties.width = 0;
    base.bounceNode.properties.height = 0;
    if (base.bounceNode.widgets) {
      base.bounceNode.widgets.forEach(w => {
        if (w.name === "Width") w.value = 0;
        if (w.name === "Height") w.value = 0;
      });
    }
  },

  "typewriter": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 195], textRend: [540, 48],
      bounceNode: [540, 205], patternNode: [540, 420], timingNode: [540, 600],
      colorNode: [540, 820], gridAnim: [820, 50],
    });
    const typewriter = createNode("effects/Typewriter", 290, 50);
    typewriter.properties.speed = 3;
    typewriter.properties.loop = true;
    typewriter.properties.pauseAtEnd = 60;
    if (typewriter.widgets) {
      typewriter.widgets.forEach(w => {
        if (w.name === "Loop") w.value = true;
      });
    }
    base.textNode.disconnectOutput(0);
    base.textNode.connect(0, typewriter, 0);
    typewriter.connect(0, base.textRend, 0);
    base.fontNode.connect(0, base.textRend, 1);
  },

  "sequenced": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 45],
      bounceNode: [555, 118], patternNode: [555, 340], timingNode: [555, 520],
      colorNode: [555, 740], gridAnim: [830, 48],
    });
    const seq = createNode("generators/Sequencer", 50, 318);
    seq.properties.bpm = 140;
    seq.properties.steps = [0.3, 0.6, 0.2, 1, 0.4, 0.8, 0.1, 0.9];
    if (seq.widgets) {
      seq.widgets.forEach(w => { if (w.name === "BPM") w.value = 140; });
    }
    const remap = createNode("utility/Remap", 50, 450);
    remap.properties.outMin = 100; remap.properties.outMax = 300;
    if (remap.widgets) {
      remap.widgets.forEach(w => {
        if (w.name === "Out Min") w.value = 100; if (w.name === "Out Max") w.value = 300;
      });
    }
    const spring = createNode("generators/Spring", 50, 625);
    spring.properties.tension = 0.5;
    spring.properties.friction = 0.7;
    seq.connect(0, remap, 0);
    remap.connect(0, spring, 0);
    spring.connect(0, base.textRend, 2);
  },

  "trail-mask": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 46],
      bounceNode: [555, 120], patternNode: [555, 342], timingNode: [555, 525],
      colorNode: [555, 745], gridAnim: [1055, 48],
    });
    const trail = createNode("input/MouseTrail", 300, 310);
    trail.properties.brushSize = 80;
    trail.properties.decay = 0.985;
    if (trail.widgets) {
      trail.widgets.forEach(w => {
        if (w.name === "Brush Size") w.value = 80;
        if (w.name === "Decay") w.value = 0.985;
      });
    }
    const mask = createNode("composite/Mask", 790, 130);
    base.bounceNode.connect(0, mask, 0);
    trail.connect(0, mask, 1);
    mask.connect(0, base.gridAnim, 0);
  },

  "gradient-blend": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 172], textRend: [302, 48],
      bounceNode: [558, 122], patternNode: [558, 348], timingNode: [558, 530],
      colorNode: [558, 750], gridAnim: [1058, 50],
    });
    const osc = createNode("generators/Oscillator", 50, 318);
    osc.properties.min = 0; osc.properties.max = 360;
    osc.properties.frequency = 0.05; osc.properties.waveform = "saw";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 0; if (w.name === "Max") w.value = 360; if (w.name === "Freq (Hz)") w.value = 0.05; });
    }
    const grad = createNode("generators/Gradient", 300, 310);
    grad.properties.type = "conic";
    grad.properties.color1 = "#ff3366";
    grad.properties.color2 = "#3366ff";
    grad.properties.color3 = "#33ff66";
    grad.properties.threeColor = true;
    if (grad.widgets) {
      grad.widgets.forEach(w => {
        if (w.name === "Type") w.value = "conic";
        if (w.name === "3 Colors") w.value = true;
        if (w.name === "Color 1") w.value = "#ff3366";
        if (w.name === "Color 2") w.value = "#3366ff";
        if (w.name === "Color 3") w.value = "#33ff66";
      });
    }
    osc.connect(0, grad, 0);

    const blend = createNode("composite/BlendMode", 790, 132);
    blend.properties.mode = "screen";
    blend.properties.opacity = 0.6;
    if (blend.widgets) {
      blend.widgets.forEach(w => {
        if (w.name === "Mode") w.value = "screen";
        if (w.name === "Opacity") w.value = 0.6;
      });
    }
    base.bounceNode.connect(0, blend, 0);
    grad.connect(0, blend, 1);
    blend.connect(0, base.gridAnim, 0);
  },

  "clock-type": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 168], textRend: [298, 46],
      bounceNode: [556, 118], patternNode: [556, 338], timingNode: [556, 518],
      colorNode: [556, 738], gridAnim: [830, 48],
    });
    const clock = createNode("generators/Clock", 50, 315);
    base.textNode.disconnectOutput(0);
    clock.connect(3, base.textRend, 0);
    const osc = createNode("generators/Oscillator", 50, 450);
    osc.properties.min = 175; osc.properties.max = 225;
    osc.properties.frequency = 1; osc.properties.waveform = "sine";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 175; if (w.name === "Max") w.value = 225; if (w.name === "Freq (Hz)") w.value = 1; });
    }
    osc.connect(0, base.textRend, 2);
    setProps(base.timingNode, { duration: 100 });
  },

  "stagger-cascade": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 48],
      bounceNode: [560, 120], patternNode: [560, 345], timingNode: [560, 528],
      colorNode: [560, 748], gridAnim: [835, 50],
    });
    const osc = createNode("generators/Oscillator", 50, 318);
    osc.properties.min = -30; osc.properties.max = 30;
    osc.properties.frequency = 0.5; osc.properties.waveform = "sine";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = -30; if (w.name === "Max") w.value = 30; if (w.name === "Freq (Hz)") w.value = 0.5; });
    }
    const stagger = createNode("generators/Stagger", 300, 315);
    stagger.properties.delay = 15;
    if (stagger.widgets) {
      stagger.widgets.forEach(w => { if (w.name === "Delay") w.value = 15; });
    }
    osc.connect(0, stagger, 0);
    stagger.connect(0, base.bounceNode, 1);
    stagger.connect(1, base.bounceNode, 2);
    stagger.connect(2, base.bounceNode, 3);
    stagger.connect(3, base.bounceNode, 4);
  },

  "random-walk": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 168], textRend: [298, 46],
      bounceNode: [556, 118], patternNode: [556, 340], timingNode: [556, 520],
      colorNode: [556, 740], gridAnim: [830, 48],
    });
    const randX = createNode("generators/Random", 50, 318);
    randX.properties.min = -50; randX.properties.max = 50;
    randX.properties.mode = "smooth"; randX.properties.interval = 45;
    if (randX.widgets) {
      randX.widgets.forEach(w => {
        if (w.name === "Mode") w.value = "smooth";
        if (w.name === "Min") w.value = -50; if (w.name === "Max") w.value = 50;
        if (w.name === "Interval") w.value = 45;
      });
    }
    const randY = createNode("generators/Random", 50, 472);
    randY.properties.min = -40; randY.properties.max = 40;
    randY.properties.mode = "smooth"; randY.properties.interval = 60;
    if (randY.widgets) {
      randY.widgets.forEach(w => {
        if (w.name === "Mode") w.value = "smooth";
        if (w.name === "Min") w.value = -40; if (w.name === "Max") w.value = 40;
        if (w.name === "Interval") w.value = 60;
      });
    }
    const randSize = createNode("generators/Random", 298, 318);
    randSize.properties.min = 125; randSize.properties.max = 275;
    randSize.properties.mode = "smooth"; randSize.properties.interval = 80;
    if (randSize.widgets) {
      randSize.widgets.forEach(w => {
        if (w.name === "Mode") w.value = "smooth";
        if (w.name === "Min") w.value = 125; if (w.name === "Max") w.value = 275;
        if (w.name === "Interval") w.value = 80;
      });
    }
    randX.connect(0, base.bounceNode, 1);
    randY.connect(0, base.bounceNode, 2);
    randSize.connect(0, base.textRend, 2);
    base.bounceNode.properties.width = 0;
    base.bounceNode.properties.height = 0;
    if (base.bounceNode.widgets) {
      base.bounceNode.widgets.forEach(w => {
        if (w.name === "Width") w.value = 0;
        if (w.name === "Height") w.value = 0;
      });
    }
  },

  "vhs": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 172], textRend: [302, 48],
      bounceNode: [558, 122], patternNode: [558, 348], timingNode: [558, 530],
      colorNode: [558, 750], gridAnim: [1478, 50],
    });
    const feedback = createNode("effects/Feedback", 792, 130);
    feedback.properties.decay = 0.88;
    feedback.properties.offsetY = 2;

    const glitch = createNode("effects/Glitch", 1020, 132);
    glitch.properties.intensity = 0.3;
    glitch.properties.sliceCount = 12;
    glitch.properties.rgbOffset = 6;

    const wave = createNode("effects/WaveDistort", 1248, 128);
    wave.properties.amplitude = 8;
    wave.properties.frequency = 0.05;
    wave.properties.direction = "horizontal";
    if (wave.widgets) {
      wave.widgets.forEach(w => { if (w.name === "Direction") w.value = "horizontal"; });
    }

    const osc = createNode("generators/Oscillator", 1022, 365);
    osc.properties.min = 0; osc.properties.max = 50;
    osc.properties.frequency = 0.1; osc.properties.waveform = "saw";
    if (osc.widgets) {
      osc.widgets.forEach(w => { if (w.name === "Min") w.value = 0; if (w.name === "Max") w.value = 50; if (w.name === "Freq (Hz)") w.value = 0.1; });
    }

    base.bounceNode.connect(0, feedback, 0);
    feedback.connect(0, glitch, 0);
    glitch.connect(0, wave, 0);
    wave.connect(0, base.gridAnim, 0);
    osc.connect(0, wave, 3);
  },

  "scroll-scrub": (base) => {
    layoutNodes(base, {
      textNode: [50, 50], fontNode: [50, 170], textRend: [300, 46],
      bounceNode: [558, 118], patternNode: [558, 340], timingNode: [558, 520],
      colorNode: [558, 740], gridAnim: [832, 48],
    });
    const scroll = createNode("input/Scroll", 300, 312);
    scroll.properties.mode = "accumulate";
    scroll.properties.min = 0; scroll.properties.max = 1;
    scroll.properties.sensitivity = 0.5;
    if (scroll.widgets) {
      scroll.widgets.forEach(w => {
        if (w.name === "Mode") w.value = "accumulate";
        if (w.name === "Sensitivity") w.value = 0.5;
        if (w.name === "Min") w.value = 0; if (w.name === "Max") w.value = 1;
      });
    }
    base.timingNode.properties.play = false;
    if (base.timingNode.widgets) {
      base.timingNode.widgets.forEach(w => { if (w.name === "Play") w.value = false; });
    }
    scroll.connect(0, base.gridAnim, 2);
  },
};

// Remember user's text across preset switches
let _userText = null;

window.loadPreset = loadPreset;
function loadPreset(name) {
  // Save current text before clearing
  const nodes = glob.graph._nodes || [];
  for (const n of nodes) {
    if (n.type === 'input/TextInput' && n.properties.text) {
      _userText = n.properties.text;
      break;
    }
  }

  glob.graph.stop();
  glob.graph.clear();

  const base = buildBaseGraph();
  const presetFn = GRAPH_PRESETS[name] || GRAPH_PRESETS["default"];
  presetFn(base);

  // Restore user's text if they edited it this session
  if (_userText) {
    base.textNode.properties.text = _userText;
  }

  glob.graph.start();
  setTimeout(fitGraphToView, 50);

  // Update URL — only store the preset name
  updateUrlParam("preset", name);

  // Highlight active preset button
  document.querySelectorAll('.preset-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.preset === name);
  });
}

function loadRandomPreset() {
  glob.graph.stop();
  glob.graph.clear();
  const base = buildBaseGraph();

  // Random generators to add (1–3)
  const generatorTypes = ["generators/Oscillator", "generators/WindData", "generators/AudioReactive"];
  const waveforms = ["sine", "square", "triangle", "saw"];

  // Possible drivable targets: [node, inputIndex, description]
  const targets = [
    { node: base.textRend, slot: 2, label: "Font Size", min: 80, max: 300 },
    { node: base.bounceNode, slot: 1, label: "Offset X" },
    { node: base.bounceNode, slot: 2, label: "Offset Y" },
    { node: base.bounceNode, slot: 3, label: "Bounce Width", min: 0, max: 100 },
    { node: base.bounceNode, slot: 4, label: "Bounce Height", min: 0, max: 100 },
    { node: base.bounceNode, slot: 5, label: "Bounce Speed", min: 1, max: 200 },
    { node: base.patternNode, slot: 1, label: "Grid Count", min: 30, max: 200 },
    { node: base.patternNode, slot: 2, label: "Cell Scale", min: 0.5, max: 4 },
  ];

  const numGenerators = 1 + Math.floor(Math.random() * 3);
  const usedTargets = new Set();
  let y = 380;

  for (let i = 0; i < numGenerators; i++) {
    const genType = generatorTypes[Math.floor(Math.random() * generatorTypes.length)];
    const gen = createNode(genType, 60 + i * 240, y);

    if (genType === "generators/Oscillator") {
      // Pick random target
      let targetIdx;
      do { targetIdx = Math.floor(Math.random() * targets.length); } while (usedTargets.has(targetIdx) && usedTargets.size < targets.length);
      usedTargets.add(targetIdx);

      const t = targets[targetIdx];
      const min = t.min != null ? t.min * (0.3 + Math.random() * 0.7) : -50;
      const max = t.max != null ? t.max * (0.5 + Math.random() * 0.5) : 50;
      const freq = 0.1 + Math.random() * 4;
      const waveform = waveforms[Math.floor(Math.random() * waveforms.length)];

      gen.properties.min = Math.round(min * 10) / 10;
      gen.properties.max = Math.round(max * 10) / 10;
      gen.properties.frequency = Math.round(freq * 100) / 100;
      gen.properties.waveform = waveform;
      if (gen.widgets) {
        gen.widgets.forEach(w => {
          if (w.name === "Min") w.value = gen.properties.min;
          if (w.name === "Max") w.value = gen.properties.max;
          if (w.name === "Freq (Hz)") w.value = gen.properties.frequency;
          if (w.name === "Waveform") w.value = gen.properties.waveform;
        });
      }
      gen.connect(0, t.node, t.slot);

    } else if (genType === "generators/WindData") {
      // Wire wind to bounce offset
      gen.connect(0, base.bounceNode, 1);
      gen.connect(1, base.bounceNode, 2);
      usedTargets.add(1); usedTargets.add(2); // mark offset targets used

    } else if (genType === "generators/AudioReactive") {
      gen.properties.active = true;
      gen.properties.gain = 1 + Math.random() * 3;
      // Wire amplitude to a random target
      let targetIdx;
      do { targetIdx = Math.floor(Math.random() * targets.length); } while (usedTargets.has(targetIdx) && usedTargets.size < targets.length);
      usedTargets.add(targetIdx);
      gen.connect(0, targets[targetIdx].node, targets[targetIdx].slot);
    }

    y += 150;
  }

  glob.graph.start();
  updateUrlParam("preset", "lucky");
}

// ─── Graph State → URL Hash ─────────────────────────────────────────────
// The full graph (nodes, connections, widget values) is encoded into the
// URL hash so you can share any custom setup by copying the link.

// Bump this when presets change meaningfully so old saved graphs are discarded
const GRAPH_VERSION = 2;


function loadGraphFromHash() {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return false;
    const json = decodeURIComponent(escape(atob(hash)));
    const graphData = JSON.parse(json);
    if (graphData._version !== GRAPH_VERSION) return false;
    glob.graph.configure(graphData);
    // Find the GridAnimator output node
    const nodes = glob.graph._nodes || [];
    for (const n of nodes) {
      if (n.type === "custom/GridAnimator") {
        glob.outputNode = n;
        break;
      }
    }
    return !!glob.outputNode;
  } catch (e) {
    return false;
  }
}

// Share button — serializes the current graph into the URL hash and copies
// it to the clipboard. Replaces the old auto-save behavior.
window.shareGraph = async function shareGraph() {
  try {
    const serialized = glob.graph.serialize();
    serialized._version = GRAPH_VERSION;
    const json = JSON.stringify(serialized);
    const encoded = btoa(unescape(encodeURIComponent(json)));
    const url = window.location.origin + window.location.pathname + window.location.search + '#' + encoded;
    history.replaceState(null, '', url);
    await navigator.clipboard.writeText(url);
    return true;
  } catch (e) {
    console.warn('[shareGraph] failed:', e);
    return false;
  }
};

function createNode(type, x, y) {
  const node = LiteGraph.createNode(type);
  node.pos = [x, y];
  glob.graph.add(node);
  return node;
}

function updateDisplay() {
  if (!glob.outputNode || !glob.outputNode.canvas) {
    requestAnimationFrame(updateDisplay);
    return;
  }

  const outCanvas = glob.outputNode.canvas;

  if (recording) {
    // Sync recording canvas to current output size
    if (testCanvas.width !== outCanvas.width || testCanvas.height !== outCanvas.height) {
      testCanvas.width = outCanvas.width;
      testCanvas.height = outCanvas.height;
    }

    if (exportFormat.value === 'png') {
      // Copy to temp canvas for png export
      testCtx.clearRect(0, 0, testCanvas.width, testCanvas.height);
      testCtx.drawImage(outCanvas, 0, 0);
      testCanvas.toBlob(blob => {
        zip.file(`frame_${String(frameCounter).padStart(5, '0')}.png`, blob);
        frameCounter++;
      }, 'image/png');

    } else if (exportFormat.value === 'webm') {
      testCtx.clearRect(0, 0, testCanvas.width, testCanvas.height);
      testCtx.drawImage(outCanvas, 0, 0);
      frameCounter++;

    } else if (exportFormat.value === 'gif') {
      gifCtx.clearRect(0, 0, gifCanvas.width, gifCanvas.height);
      gifCtx.drawImage(outCanvas, 0, 0, gifCanvas.width, gifCanvas.height);
      gif.addFrame(gifCanvas, { delay: 1000 / 60, copy: true });
    }
  }

  requestAnimationFrame(updateDisplay);
}

if (recordBtn) recordBtn.onclick = async () => {
  recording = !recording;
  recordBtn.textContent = recording ? 'Stop' : 'Record';

  if (recording) {
    frameCounter = 0;

    if (exportFormat.value === 'webm') {
      recordedBlobs = [];
      const stream = testCanvas.captureStream(60);
      mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm; codecs=vp9' });
      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) recordedBlobs.push(event.data);
      };
      try {
        mediaRecorder.start();
      } catch (error) {
        console.error('Failed to start MediaRecorder:', error);
        recording = false;
        recordBtn.textContent = 'Record';
      }
    } else if (exportFormat.value === 'png') {
      const { default: JSZip } = await import('jszip');
      zip = new JSZip();
    } else if (exportFormat.value === 'gif') {
      const { default: GIF } = await import('gif.js.optimized');

      let colour = getUrlParam("animationColour") || '#010101';
      setGlobalConfig("brushColor", colour);

      gif = new GIF({
        workers: 2,
        workerScript: '/js/gif.worker.js',
        quality: 10,
        width: gifSize,
        height: gifSize,
        transparent: '0x000000'
      });

      gif.on('finished', function(blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'animation.gif';
        a.click();
        URL.revokeObjectURL(url);
      });
    }

  } else {
    if (exportFormat.value === 'webm') {
      mediaRecorder.stop();
      mediaRecorder.onstop = () => {
        const videoBlob = new Blob(recordedBlobs, { type: 'video/webm' });
        const url = URL.createObjectURL(videoBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'recording.webm';
        a.click();
        URL.revokeObjectURL(url);
      };
    } else if (exportFormat.value === 'png') {
      zip.generateAsync({ type: 'blob' }, metadata => {
        recordBtn.textContent = `Processing: ${Math.round(metadata.percent)}%`;
      }).then(content => {
        recordBtn.textContent = 'Record';
        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'frames.zip';
        a.click();
        URL.revokeObjectURL(url);
      });
    } else if (exportFormat.value === 'gif') {
      gif.render();
    }
  }
};



  function getUrlParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

 function updateUrlParam(key, value) {
    const url = new URL(window.location);
    url.searchParams.set(key, value);
    window.history.replaceState({}, '', url);
  }


const brushPickerEl = document.getElementById("brushColorPicker");
if (brushPickerEl) {
  brushPickerEl.addEventListener("input", (event) => {
    setGlobalConfig("brushColor", event.target.value);
  });
}


window.addEventListener("resize", () => {
  const wrap = document.getElementById('node-wrap');
  nodeEditor.width = wrap.clientWidth;
  nodeEditor.height = wrap.clientHeight;
  glob.graphCanvas.resize();
});

// ─── Sidebar drag-to-canvas ───────────────────────────────────────────────
function setupSidebarDrag() {
  const sidebar = document.getElementById('node-sidebar');
  if (!sidebar) return;

  sidebar.addEventListener('dragstart', (e) => {
    const nodeEl = e.target.closest('.sidebar-node');
    if (!nodeEl) return;
    e.dataTransfer.setData('text/plain', nodeEl.dataset.type);
    e.dataTransfer.effectAllowed = 'copy';
  });

  // Allow drop on the canvas
  nodeEditor.addEventListener('dragover', (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  });

  nodeEditor.addEventListener('drop', (e) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('text/plain');
    if (!nodeType || !glob.graphCanvas) return;

    // Convert drop position to graph coordinates
    const rect = nodeEditor.getBoundingClientRect();
    const x = (e.clientX - rect.left) / glob.graphCanvas.ds.scale - glob.graphCanvas.ds.offset[0];
    const y = (e.clientY - rect.top) / glob.graphCanvas.ds.scale - glob.graphCanvas.ds.offset[1];

    const node = LiteGraph.createNode(nodeType);
    if (node) {
      node.pos = [x, y];
      glob.graph.add(node);
      glob.graphCanvas.selectNode(node);
    }
  });
}

document.addEventListener("DOMContentLoaded", () => {
  // Explicitly load all project fonts before starting the canvas —
  // CSS @font-face declarations only trigger a fetch on first use,
  // so the canvas can render before fonts are ready.
  const fontsToLoad = [
    new FontFace('Frame', "url('assets/fonts/Crany_Frame.otf')"),
    new FontFace('Hairline', "url('assets/fonts/Cranny-Hairline.otf')"),
    new FontFace('Up front', "url('assets/old_fonts/Cranny-UpFront.ttf')"),
    new FontFace('ParadigmShifts', "url('assets/old_fonts/Cranny-ParadigmShifts.ttf')"),
    new FontFace('Visionaries', "url('assets/old_fonts/Cranny-Visionaries.ttf')"),
    new FontFace('Blocks', "url('assets/old_fonts/Cranny-Blocks.ttf')"),
  ];

  fontsToLoad.forEach(f => document.fonts.add(f));

  Promise.all(
    fontsToLoad.map(f => f.load().catch(err => console.warn('[app.js] Font load failed:', f.family, err)))
  ).then(() => {
    buildChart();
    setupSidebarDrag();

    // Preset buttons (skip article presets — they open the drawer instead)
    document.querySelectorAll('.preset-btn:not(.preset-btn--article)').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const preset = btn.dataset.preset;
        if (preset === 'lucky') {
          loadRandomPreset();
          document.querySelectorAll('.preset-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
        } else {
          loadPreset(preset);
        }
      });
    });

    // Article preset buttons load the preset effect
    document.querySelectorAll('.preset-btn--article').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const preset = btn.dataset.preset;
        loadPreset(preset);
      });
    });
  }); // fonts loaded
});
